import { horizontalEllipsis } from '@ipfabric/frontend-shared-assets/utils/characters';
import { FormDatepicker } from '@ipfabric/ui-components/FormDatepicker/FormDatepicker';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { Link } from '@ipfabric/ui-components/Link/Link';
import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import {
  type DaysLimit,
  type DiscoveryHistorySeedFormValues,
  type NumericDaysLimit,
  daysLimitValues,
} from 'components/DiscoverySeed/DiscoverySeed';
import { format, parse } from 'date-fns';
import { useWatch } from 'react-hook-form';
import z from 'zod';
import type { DiscoverySettingsObject } from '../../model/DiscoverySettingsObject';

export function convertDaysLimitToFormValue(
  daysLimit?: number | null,
  afterDate?: string | null,
): DaysLimit {
  if (daysLimit !== undefined && daysLimit !== null) {
    return daysLimit.toString() as NumericDaysLimit;
  }
  return afterDate ? 'custom' : 'all';
}

export function convertAfterDateToFormValue(
  afterDate?: string | null,
): Date | null {
  if (afterDate) {
    return parse(afterDate, 'yyyy-MM-dd', new Date());
  }
  return null;
}

export const ipSeedHistory: FormSnippet<
  DiscoverySettingsObject,
  DiscoveryHistorySeedFormValues,
  DiscoveryHistorySeedFormValues,
  { enabled: boolean; daysLimit: DaysLimit }
> = {
  render: ({ form: { control }, hook }) => {
    const { enabled, daysLimit } = hook;
    return (
      <div className='py px-sm'>
        <FormSwitch
          control={control}
          name='enabled'
          label='Use previously discovered devices as seeds'
          description={
            <>
              Previously discovered devices are listed in the{' '}
              <Link renderAs='link' to='/management/discovery-history'>
                Discovery history table.
              </Link>
            </>
          }
        />
        <div className='flex flex-wrap gap-2.5 mt-xs pl-12'>
          <div className='w-60'>
            <FormSelect.Mandatory
              control={control}
              name='daysLimit'
              disabled={!enabled}
              options={[
                { label: 'Use entire history table', value: 'all' },
                { label: 'Devices discovered in last 7 days', value: '7' },
                { label: 'Devices discovered in last 14 days', value: '14' },
                { label: 'Devices discovered in last 30 days', value: '30' },
                { label: 'Devices discovered in last 3 months', value: '90' },
                {
                  label: `Devices discovered after ${horizontalEllipsis}`,
                  value: 'custom',
                },
              ]}
            />
          </div>
          <div className='w-60'>
            {daysLimit === 'custom' && (
              <FormDatepicker
                control={control}
                name='afterDate'
                className='w-60'
                disabled={!enabled}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
  use: ({ form }) => {
    const enabled = useWatch({ control: form.control, name: 'enabled' });
    const daysLimit = useWatch({ control: form.control, name: 'daysLimit' });
    return { enabled, daysLimit };
  },
  fromModel: (model, fieldValuesDraft) => {
    fieldValuesDraft.enabled = model.discoveryHistorySeeds?.enabled ?? false;
    fieldValuesDraft.daysLimit = convertDaysLimitToFormValue(
      model.discoveryHistorySeeds?.daysLimit,
      model.discoveryHistorySeeds?.afterDate,
    );
    fieldValuesDraft.afterDate = convertAfterDateToFormValue(
      model.discoveryHistorySeeds?.afterDate,
    );
  },
  toModel: (transformedValues, modelDraft) => {
    modelDraft.discoveryHistorySeeds = {
      enabled: transformedValues.enabled,
      daysLimit:
        transformedValues.daysLimit === 'custom' ||
        transformedValues.daysLimit === 'all'
          ? null
          : Number(transformedValues.daysLimit),
      afterDate:
        transformedValues.daysLimit === 'custom' && transformedValues.afterDate
          ? format(transformedValues.afterDate, 'yyyy-MM-dd')
          : null,
    };
  },
  schema: z.object({
    enabled: z.boolean(),
    daysLimit: z.enum(daysLimitValues),
    afterDate: z.date().nullable(),
  }),
};
